'use client'

import { formatEventDateRange } from '@/lib/timed-ui/formatting'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { parseVideoUrl } from '@/lib/video-embed'
import type { TimedUIEvent } from '@/lib/timed-ui/adapters'

interface EventContentSectionProps {
  event: TimedUIEvent
}

const EventContentSection = ({ event }: EventContentSectionProps) => {
  const formatPrice = useFormatPrice()
  if (!event) return null

  const hasDescription = event.description && event.description.trim().length > 0
  const locationName = event.venue?.name || ''
  const locationAddress = event.venue
    ? [event.venue.address, event.venue.city, event.venue.state].filter(Boolean).join(', ')
    : ''
  // Filter FIRST, then check: the length test was on the unfiltered list, so an
  // event whose visible tickets are all free or all add-ons (a sponsor's comp
  // allocation, a free event) reduced an empty array and Math.min() returned
  // Infinity -- the details card read "From $∞".
  const pricedTickets = event.ticketTypes.filter(t => t.allInPrice > 0 && !t.isAddon)
  const minPrice = pricedTickets.length > 0
    ? Math.min(...pricedTickets.map(t => t.allInPrice))
    : 0
  // Every admission ticket on offer is free (a comp sponsor link, a free event).
  const allFree = pricedTickets.length === 0 && event.ticketTypes.some(t => !t.isAddon)
  const isVideoBanner = event.bannerDisplayMode === 'video'
  const hasVideo = !isVideoBanner && event.videoUrl ? !!parseVideoUrl(event.videoUrl) : false

  return (
    <div className="space-y-6 overflow-x-hidden max-w-full">
      <section className="border-l-4 border-primary pl-5">
        <h2 className="text-h2 font-heading font-bold text-text-primary mb-3">
          About This Experience
        </h2>
        {hasDescription ? (
          <div
            className="description-html text-text-secondary leading-relaxed max-w-full overflow-hidden"
            dangerouslySetInnerHTML={{ __html: event.description }}
          />
        ) : (
          <div className="text-text-secondary leading-relaxed space-y-3">
            <p>
              Join us for <strong className="text-text-primary">{event.title}</strong> -- an unforgettable
              experience you won&apos;t want to miss.
            </p>
            <p>
              Tickets are limited and sell out quickly. Book now to guarantee your entry.
            </p>
          </div>
        )}
        {hasVideo && (
          <button
            type="button"
            onClick={() => document.getElementById('event-video')?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            Watch Video
          </button>
        )}
      </section>

      <section className="border-t border-surface-border pt-6">
        <h2 className="text-h2 font-heading font-bold text-text-primary mb-3">Event Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {event.startDate && (
            <div className="flex items-start gap-3 p-3 bg-surface-secondary rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <div className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-0.5">
                  {event.endDate ? 'Dates' : 'Date'}
                </div>
                <div className="font-semibold text-text-primary text-sm">
                  {formatEventDateRange(event.startDate, event.endDate, true, event.timezone)}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 p-3 bg-surface-secondary rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <div className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-0.5">Location</div>
              {locationName ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([locationName, locationAddress].filter(Boolean).join(', '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group/loc"
                >
                  <div className="font-semibold text-text-primary text-sm group-hover/loc:text-primary group-hover/loc:underline transition-colors">{locationName}</div>
                  {locationAddress && <div className="text-sm text-text-secondary mt-0.5 group-hover/loc:text-primary/70 transition-colors">{locationAddress}</div>}
                </a>
              ) : (
                <div className="font-semibold text-text-primary text-sm">Venue details provided after booking</div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-surface-secondary rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <div>
              <div className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-0.5">Tickets</div>
              <div className="font-semibold text-text-primary text-sm">
                {event.status === 'SOLD_OUT' ? 'Sold out' : minPrice > 0 ? `From ${formatPrice(minPrice)}` : allFree ? 'Free' : 'Select date for pricing'}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-surface-border pt-6">
        <h2 className="text-h2 font-heading font-bold text-text-primary mb-3">Good to Know</h2>
        <div className="space-y-2.5">
          {[
            'Instant confirmation after booking',
            'Mobile tickets -- no printing needed',
            'Confirmation email sent to your inbox',
          ].map((text, i) => (
            <div key={i} className="flex items-center gap-3 text-sm px-4 py-2.5 rounded-lg bg-[rgb(var(--color-muted-bg))] text-[rgb(var(--color-muted-text))]">
              <div className="w-5 h-5 rounded-full bg-current/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="font-medium">{text}</span>
            </div>
          ))}
        </div>
      </section>

      {event.videoUrl && !isVideoBanner && (() => {
        const video = parseVideoUrl(event.videoUrl, event.videoEmbedOptions)
        return video ? (
          <section id="event-video" className="border-t border-surface-border pt-6 scroll-mt-24">
            <h2 className="text-h2 font-heading font-bold text-text-primary mb-3">Watch</h2>
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              <iframe
                src={video.embedUrl}
                title={`${event.title} video`}
                className="w-full h-full"
                allowFullScreen
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          </section>
        ) : null
      })()}
    </div>
  )
}

export default EventContentSection
